import React, { useState, useEffect, useRef } from 'react';
import './Popup.scss';

interface PopupFooterButton {
  text: string;
  bgColor?: string;
  textColor?: string;
  onClick: () => void;
  disabled?: boolean;
}

interface PopupProps {
  isVisible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  isLoading?: boolean;
  showCloseButton?: boolean;
  autoCloseIn?: number;
  className?: string;
  maxWidth?: string;
  maxHeight?: string;
  backdropClickClose?: boolean;
  animationDuration?: number;
  showFooter?: boolean;
  showButton1?: boolean;
  button1Config?: PopupFooterButton;
  showButton2?: boolean;
  button2Config?: PopupFooterButton;
}

const Popup: React.FC<PopupProps> = ({
  isVisible,
  onClose,
  children,
  isLoading = false,
  showCloseButton = true,
  autoCloseIn,
  className = '',
  maxWidth = '100vw',
  maxHeight = '80vh',
  backdropClickClose = true,
  animationDuration = 300,
  showFooter = false,
  showButton1 = false,
  button1Config,
  showButton2 = false,
  button2Config,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      
      if (autoCloseIn && autoCloseIn > 0) {
        autoCloseTimeoutRef.current = setTimeout(() => {
          handleClose();
        }, autoCloseIn);
      }
    } else {
      setIsAnimating(false);
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    }

    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
    };
  }, [isVisible, autoCloseIn]);

  const handleClose = () => {
    if (isLoading) return;
    
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, animationDuration);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (backdropClickClose && e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`popup ${className}`}
      style={{
        '--animation-duration': `${animationDuration}ms`,
        '--max-width': maxWidth,
        '--max-height': maxHeight,
      } as React.CSSProperties}
    >
      <div 
        className="popup__overlay"
        onClick={handleBackdropClick}
      >
        <div 
          className={`popup__content ${isAnimating ? 'popup__content--animating' : ''}`}
        >
          {isLoading && (
            <div className="popup__loading-overlay">
              <div className="popup__loading-spinner" />
            </div>
          )}

          {showCloseButton && (
            <button
              onClick={handleClose}
              disabled={isLoading}
              className={`popup__close-button ${isLoading ? 'popup__close-button--disabled' : ''}`}
            >
              ×
            </button>
          )}

          <div className="popup__body">
            {children}
          </div>

          {showFooter && (
            <div className="popup__footer">
              {showButton1 && button1Config && (
                <button
                  onClick={button1Config.onClick}
                  disabled={isLoading || button1Config.disabled}
                  className="popup__footer-button popup__footer-button--primary"
                  style={{
                    backgroundColor: button1Config.bgColor || '#3c3c3c',
                    color: button1Config.textColor || '#ffffff'
                  }}
                >
                  {button1Config.text}
                </button>
              )}
              
              {showButton2 && button2Config && (
                <button
                  onClick={button2Config.onClick}
                  disabled={isLoading || button2Config.disabled}
                  className="popup__footer-button popup__footer-button--secondary"
                  style={{
                    backgroundColor: button2Config.bgColor || 'transparent',
                    color: button2Config.textColor || '#666666'
                  }}
                >
                  {button2Config.text}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Popup;